import { createIdempotencyKey, type ApiClient } from "@decizyon/api-client";

import {
  RealtimeClient,
  type RealtimeEventEnvelope,
} from "../../../../../packages/support-chat/src/realtime";

export const TICKET_BOARD_EVENT_NAME = "decizyon:ticket-board-event";

type TicketBoardResource = "all" | `product:${string}`;

interface ConnectionTicketResponse {
  readonly expiresIn: 30;
  readonly ticket: string;
}

function createClientInstanceId(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function realtimeUrl(configuredUrl?: string | URL): URL {
  if (configuredUrl !== undefined) {
    return new URL(configuredUrl.toString());
  }
  const url = new URL("/api/v1/realtime", window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url;
}

export function subscribeToTicketBoard(
  apiClient: ApiClient,
  resourceId: TicketBoardResource,
  configuredRealtimeUrl?: string | URL,
): () => void {
  const clientInstanceId = createClientInstanceId();
  let attempt = 0;

  const dispatchBoardEvent = (detail: Readonly<Record<string, unknown>> = {}): void => {
    window.dispatchEvent(new CustomEvent(TICKET_BOARD_EVENT_NAME, { detail }));
  };
  const issueTicket = async (): Promise<string> => {
    attempt += 1;
    const response = await apiClient.mutate<ConnectionTicketResponse, {
      readonly clientInstanceId: string;
      readonly origin: string;
    }>("realtime/connection-tickets", {
      body: { clientInstanceId, origin: window.location.origin },
      idempotencyKey: createIdempotencyKey(`ticket-board-connection-${clientInstanceId}-${attempt}`),
      method: "POST",
      protectedScope: "capability:tickets:read",
    });
    if (response.ticket === "" || response.expiresIn <= 0) {
      throw new TypeError("Realtime connection ticket response is invalid");
    }
    return response.ticket;
  };
  let client: RealtimeClient | undefined;
  client = new RealtimeClient({
    channel: "ticket_board",
    clientInstanceId,
    initialSequence: 0,
    issueTicket,
    onEvent: async (event: RealtimeEventEnvelope) => {
      dispatchBoardEvent(event.data);
      return event.stream.sequence;
    },
    onResync: async (_recoveryUrl, currentSequence) => {
      dispatchBoardEvent();
      return currentSequence ?? client?.lastSequence ?? 0;
    },
    requestIdPrefix: "ticket-board",
    resourceId,
    sessionId: resourceId,
    streamType: "ticket_board",
    url: realtimeUrl(configuredRealtimeUrl),
  });

  void client.connect();
  return () => {
    client?.close();
    client = undefined;
  };
}
